import React, { useEffect, useMemo, useState } from 'react';
import './ConfigFormModal.less';
import { Config, BlindLevel } from '../../types/config';
import { generateBlindStructure } from '../utils/generateBlindStructure';

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    existing?: Config;
}

function SortableItem({ id, children }: { id: string; children: React.ReactNode }) {
    const { setNodeRef, attributes, listeners, transform, transition } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    return (
        <div ref={setNodeRef} style={style} className="flex items-center gap-2 mb-2">
      <span
          {...attributes}
          {...listeners}
          className="cursor-move text-gray-500 px-2 select-none"
          title="Ziehen, um Reihenfolge zu ändern"
      >
        ≡
      </span>
            {children}
        </div>
    );
}

// ---------- Helpers für Draft-Zustand ----------

type BlindLevelDraft = {
    type?: 'break';
    smallBlind: string; // Strings erlauben leeres Eingeben
    bigBlind: string;
    ante: string;
    duration: string;
};

const toDraft = (b: BlindLevel): BlindLevelDraft => ({
    type: b.type === 'break' ? 'break' : undefined,
    smallBlind: String(b.smallBlind ?? 0),
    bigBlind: String(b.bigBlind ?? 0),
    ante: String(b.ante ?? 0),
    duration: String(b.duration ?? 0),
});

const fromDraft = (d: BlindLevelDraft): BlindLevel => ({
    // für breaks bleiben SB/BB/Ante 0
    type: d.type === 'break' ? 'break' : undefined,
    smallBlind: parseInt(d.smallBlind || '0', 10) || 0,
    bigBlind: parseInt(d.bigBlind || '0', 10) || 0,
    ante: parseInt(d.ante || '0', 10) || 0,
    duration: Math.max(1, parseInt(d.duration || '0', 10) || 0), // Dauer min. 1
});

const onlyDigits = (s: string) => s.replace(/\D+/g, '');
const stripLeadingZeros = (s: string) => s.replace(/^0+(?=\d)/, '');
const normalize = (s: string) => {
    const digits = onlyDigits(s);
    // keine führenden Nullen erlauben (außer alleinige "0")
    const cleaned = stripLeadingZeros(digits);
    if (digits === '') return ''; // Eingabe darf temporär leer sein
    return cleaned === '' ? '0' : cleaned;
};

const hasLeadingZero = (s: string) => s.length > 1 && s.startsWith('0');
const isDigitsOnly = (s: string) => /^\d*$/.test(s);

export default function ConfigFormModal({ isOpen, onClose, onSuccess, existing }: Props) {
    const [name, setName] = useState('');
    const [blindsDraft, setBlindsDraft] = useState<BlindLevelDraft[]>([]);
    const [error, setError] = useState('');

    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        setName(existing?.name || '');
        setBlindsDraft((existing?.blinds || []).map(toDraft));
    }, [existing]);

    const addBlindDraft = (d: Partial<BlindLevelDraft> = {}) => {
        setBlindsDraft([
            ...blindsDraft,
            {
                type: d.type,
                smallBlind: d.smallBlind ?? '0',
                bigBlind: d.bigBlind ?? '0',
                ante: d.ante ?? '0',
                duration: d.duration ?? '10',
            },
        ]);
    };

    const handleAddBlind = () => addBlindDraft();
    const handleAddBreak = () => addBlindDraft({ type: 'break', duration: '5' });

    const handleChangeDraft = (
        index: number,
        key: keyof BlindLevelDraft,
        raw: string
    ) => {
        const next = [...blindsDraft];
        // Name-Feld existiert hier nicht; alle Zahlenfelder normalisieren
        const value = key === 'type' ? (raw as any) : normalize(raw);
        (next[index] as any)[key] = value;
        setBlindsDraft(next);
    };

    const handleBlurDefaultZero = (index: number, key: keyof BlindLevelDraft) => {
        const next = [...blindsDraft];
        const v = (next[index] as any)[key];
        if (v === '') (next[index] as any)[key] = '0';
        setBlindsDraft(next);
    };

    const handleRemoveBlind = (index: number) => {
        setBlindsDraft(blindsDraft.filter((_, i) => i !== index));
    };

    // ---------- Validierung ----------
    type FieldError = { index: number; field: keyof BlindLevelDraft; reason: string };

    const fieldErrors: FieldError[] = useMemo(() => {
        const errs: FieldError[] = [];
        blindsDraft.forEach((b, i) => {
            const check = (field: keyof BlindLevelDraft, allowZero = true) => {
                const v = (b as any)[field] as string;
                if (!isDigitsOnly(v)) errs.push({ index: i, field, reason: 'Nur Ziffern erlaubt' });
                if (hasLeadingZero(v)) errs.push({ index: i, field, reason: 'Führende Null nicht erlaubt' });
                if (!allowZero && (v === '' || parseInt(v || '0', 10) <= 0)) {
                    errs.push({ index: i, field, reason: 'Muss > 0 sein' });
                }
            };
            if (b.type === 'break') {
                check('duration', false);
            } else {
                check('smallBlind');
                check('bigBlind');
                check('ante');
                check('duration', false);
            }
        });
        return errs;
    }, [blindsDraft]);

    const hasErrors = fieldErrors.length > 0;
    const isFieldInvalid = (i: number, f: keyof BlindLevelDraft) =>
        fieldErrors.some((e) => e.index === i && e.field === f);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const normalizedDraft = blindsDraft.map((d) => ({
            ...d,
            smallBlind: d.smallBlind === '' ? '0' : d.smallBlind,
            bigBlind: d.bigBlind === '' ? '0' : d.bigBlind,
            ante: d.ante === '' ? '0' : d.ante,
            duration: d.duration === '' ? '0' : d.duration,
        }));

        const tmp = normalizedDraft;
        const anyLeading = tmp.some((d) =>
            [d.smallBlind, d.bigBlind, d.ante, d.duration].some((v) => hasLeadingZero(v))
        );

        // Validierung
        if (anyLeading || hasErrors) {
            setError('Bitte Eingaben prüfen: nur Ziffern, keine führenden Nullen, Dauer > 0.');
            return;
        }
        if (!name.trim()) {
            setError('Bitte einen Namen vergeben.');
            return;
        }
        if (blindsDraft.length === 0) {
            setError('Mindestens ein Blindlevel oder eine Pause hinzufügen.');
            return;
        }

        const token = localStorage.getItem('userToken');
        const method = existing ? 'PUT' : 'POST';
        const url = existing ? `/api/user/configs/${existing._id}` : '/api/user/configs';

        const blinds: BlindLevel[] = normalizedDraft.map(fromDraft);

        const res = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ name, blinds }),
        });

        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            setError(data.message || 'Fehler beim Speichern');
            return;
        }

        onSuccess();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="config-form-modal fixed inset-0 flex justify-center items-center z-50">
            <div className="config-form-wrapper p-6 rounded shadow-lg w-full max-w-5xl overflow-y-auto max-h-[90vh]">
                <h2 className="text-2xl font-bold mb-6">{existing ? 'Config bearbeiten' : 'Neue Config'}</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block mb-1 font-semibold">Config Name</label>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full border px-3 py-2"
                            placeholder="Turniername oder Beschreibung"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block mb-1 font-semibold">Blindstruktur generieren asd</label>
                        <div className="flex gap-2">
                            <select
                                className="border px-3 py-2"
                                value=""
                                onChange={(e) => {
                                    const selected = e.target.value as 'turbo' | 'standard' | 'deepstack';
                                    if (selected) {
                                        const generated = generateBlindStructure(selected); // numbers
                                        setBlindsDraft(generated.map(toDraft));
                                    }
                                }}
                            >
                                <option value="">-- Variante wählen --</option>
                                <option value="turbo">Turbo (10 min)</option>
                                <option value="standard">Standard (20 min)</option>
                                <option value="deepstack">Deepstack (30 min)</option>
                            </select>
                            <button
                                type="button"
                                className="text-sm text-blue-600 underline"
                                onClick={() => setBlindsDraft([])}
                            >
                                Zurücksetzen
                            </button>
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="font-semibold">Blindlevels</span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleAddBlind}
                                    className="bg-green-600 text-white text-sm px-3 py-1 rounded"
                                >
                                    + Blindlevel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAddBreak}
                                    className="bg-yellow-600 text-white text-sm px-3 py-1 rounded"
                                >
                                    + Pause
                                </button>
                            </div>
                        </div>

                        {blindsDraft.length > 0 && (
                            <div className="grid grid-cols-5 gap-3 text-sm font-semibold mb-2 ml-8">
                                <div>Small Blind</div>
                                <div>Big Blind</div>
                                <div>Ante</div>
                                <div>Dauer (Minuten)</div>
                                <div></div>
                            </div>
                        )}

                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={({ active, over }) => {
                                if (!over) return;
                                if (active.id !== over.id) {
                                    const oldIndex = parseInt(String(active.id));
                                    const newIndex = parseInt(String(over.id));
                                    setBlindsDraft(arrayMove(blindsDraft, oldIndex, newIndex));
                                }
                            }}
                        >
                            <SortableContext
                                items={blindsDraft.map((_, index) => index.toString())}
                                strategy={verticalListSortingStrategy}
                            >
                                {blindsDraft.map((blind, index) => (
                                    <SortableItem key={index} id={index.toString()}>
                                        {blind.type === 'break' ? (
                                            <div className="grid grid-cols-5 gap-3 items-center flex-1 text-gray-600 italic">
                                                <div className="col-span-3">Pause</div>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    placeholder="Dauer"
                                                    value={blind.duration}
                                                    onChange={(e) => handleChangeDraft(index, 'duration', e.target.value)}
                                                    onBlur={() => handleBlurDefaultZero(index, 'duration')}
                                                    className={`border px-2 py-1 ${isFieldInvalid(index, 'duration') ? 'border-red-500' : ''}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveBlind(index)}
                                                    className="text-red-600 text-sm"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-5 gap-3 items-center flex-1">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    placeholder="Small Blind"
                                                    value={blind.smallBlind}
                                                    onChange={(e) => handleChangeDraft(index, 'smallBlind', e.target.value)}
                                                    onBlur={() => handleBlurDefaultZero(index, 'smallBlind')}
                                                    className={`border px-2 py-1 ${isFieldInvalid(index, 'smallBlind') ? 'border-red-500' : ''}`}
                                                />
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    placeholder="Big Blind"
                                                    value={blind.bigBlind}
                                                    onChange={(e) => handleChangeDraft(index, 'bigBlind', e.target.value)}
                                                    onBlur={() => handleBlurDefaultZero(index, 'bigBlind')}
                                                    className={`border px-2 py-1 ${isFieldInvalid(index, 'bigBlind') ? 'border-red-500' : ''}`}
                                                />
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    placeholder="Ante"
                                                    value={blind.ante}
                                                    onChange={(e) => handleChangeDraft(index, 'ante', e.target.value)}
                                                    onBlur={() => handleBlurDefaultZero(index, 'ante')}
                                                    className={`border px-2 py-1 ${isFieldInvalid(index, 'ante') ? 'border-red-500' : ''}`}
                                                />
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    placeholder="Dauer"
                                                    value={blind.duration}
                                                    onChange={(e) => handleChangeDraft(index, 'duration', e.target.value)}
                                                    onBlur={() => handleBlurDefaultZero(index, 'duration')}
                                                    className={`border px-2 py-1 ${isFieldInvalid(index, 'duration') ? 'border-red-500' : ''}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveBlind(index)}
                                                    className="text-red-600 text-sm"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        )}
                                    </SortableItem>
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>

                    {error && <div className="text-red-600 text-sm">{error}</div>}

                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={onClose} className="border px-4 py-2">
                            Abbrechen
                        </button>
                        <button type="submit" className="button px-4 py-2" disabled={hasErrors}>
                            {existing ? 'Speichern' : 'Erstellen'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
